import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sparkles, Clock, Users, Store, Wallet, Compass,
  Globe, ExternalLink, RotateCcw, CornerDownLeft, Loader2, AlertCircle,
} from 'lucide-react';
import { askMiloAgent } from '../../../data/api';
import './AskMiloPalette.css';

/**
 * AskMiloPalette — the "Ask Milo Anything" pill in the admin header and the
 * command palette it opens. Milo is a Claude-powered, platform-aware agent:
 *
 *   • Data questions ("how many RSVP'd?", "how much budget is left?",
 *     "what vendors are booked?") are answered from the platform's single
 *     source of truth via read-only tools — never the web.
 *   • Open-ended questions ("what else should I book for a milestone
 *     birthday?") may need the web. Milo never browses silently: it proposes
 *     a search and the host approves it first (gated web search).
 *   • Milo is read-only — it never sends, saves, or pays.
 *
 * The palette also keeps its live guest/vendor search for quick navigation.
 *
 * Keyboard: ⌘K / Ctrl+K opens anywhere · ESC closes · Enter asks Milo.
 */

// Example prompts that actually submit to Milo (no fake/hardcoded data).
const SUGGESTIONS = [
  { ic: Clock,  text: 'What needs me today?' },
  { ic: Users,  text: "How many guests have RSVP'd?" },
  { ic: Wallet, text: 'How much of my budget is left?' },
  { ic: Compass, text: 'What else should I book for a milestone birthday?' },
];

// Friendly labels for the read-only tools Milo used, shown as provenance.
const TOOL_LABELS = {
  get_event_details: 'event details',
  get_guest_summary: 'guest list',
  get_vendors: 'vendors',
  get_finance_summary: 'budget',
  get_event_readiness: 'readiness',
  get_decisions: 'decisions',
};

function friendlyTools(tools) {
  const seen = [];
  (tools || []).forEach((t) => {
    const label = TOOL_LABELS[t];
    if (label && !seen.includes(label)) seen.push(label);
  });
  return seen;
}

export default function AskMiloPalette({
  searchQuery = '',
  setSearchQuery = () => {},
  searchResults = null,
  onNavigate = () => {},
  onFocusGuest = () => {},
  initialOpen = false,
}) {
  const [open, setOpen] = useState(initialOpen);
  const [thread, setThread] = useState([]);          // [{role:'user'|'milo', ...}]
  const [loading, setLoading] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);
  const threadEndRef = useRef(null);

  const openPalette = useCallback(() => setOpen(true), []);
  const resetThread = useCallback(() => {
    setThread([]);
    setLoading(false);
    setPendingQuestion('');
  }, []);
  const closePalette = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
    resetThread();
  }, [setSearchQuery, resetThread]);

  // Focus input when palette opens.
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Keep the latest turn in view.
  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [thread, loading]);

  // Global keyboard: ⌘K / Ctrl+K toggles, ESC closes.
  useEffect(() => {
    function handleKey(e) {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        setOpen((cur) => !cur);
        return;
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        closePalette();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, closePalette]);

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) closePalette();
  };

  // Ask Milo. confirmWebSearch=true re-runs the SAME question with the host's
  // approval to use web search (replacing the prior "may I search?" turn).
  const submitQuestion = useCallback(async (q, { confirmWebSearch = false } = {}) => {
    const question = (q ?? searchQuery ?? '').trim();
    if (!question || loading) return;
    setPendingQuestion(question);
    if (!confirmWebSearch) {
      setThread((t) => [...t, { role: 'user', text: question }]);
      setSearchQuery('');
    } else {
      // Drop the pending web-confirm card; we're answering it now.
      setThread((t) => (t.length && t[t.length - 1].role === 'milo'
        ? t.slice(0, -1) : t));
    }
    setLoading(true);
    try {
      const res = await askMiloAgent(question, { confirmWebSearch });
      setThread((t) => [...t, {
        role: 'milo',
        text: res.answer || '',
        citations: res.citations || [],
        usedTools: res.used_tools || [],
        webConfirm: res.needs_web_confirmation ? (res.proposed_search || {}) : null,
        configured: res.configured !== false,
        searched: confirmWebSearch,
      }]);
    } catch (e) {
      setThread((t) => [...t, {
        role: 'milo', error: true,
        text: 'I couldn’t reach the server just now. Please try again in a moment.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, loading, setSearchQuery]);

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuestion();
    }
  };

  const hasSearchResults = searchResults && searchResults.total > 0;
  const inConversation = thread.length > 0 || loading;
  // Show live search nav only before any Milo conversation has started.
  const showSearch = !inConversation && hasSearchResults;
  const showSuggestions = !inConversation && !hasSearchResults;

  return (
    <>
      {/* The visible pill in the header. */}
      <button
        type="button"
        className="ask-milo-pill"
        onClick={openPalette}
        data-testid="ask-milo-pill"
        aria-label="Ask Milo Anything"
      >
        <Sparkles className="ask-milo-pill__star" aria-hidden="true" />
        <span className="ask-milo-pill__label">Ask Milo Anything</span>
        <span className="ask-milo-pill__kbd">Ctrl K</span>
      </button>

      {open && (
        <div
          ref={backdropRef}
          className="ask-milo-backdrop"
          onClick={handleBackdropClick}
          role="presentation"
        >
          <div
            className="ask-milo-palette"
            role="dialog"
            aria-modal="true"
            aria-label="Ask Milo command palette"
          >
            <div className="ask-milo-palette__field">
              <Sparkles className="ask-milo-palette__star" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                className="ask-milo-palette__input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder={inConversation ? 'Ask a follow-up…' : 'Ask Milo anything, or jump to any item…'}
                data-testid="ask-milo-input"
              />
              {inConversation ? (
                <button type="button" className="ask-milo-palette__reset" onClick={resetThread} title="Start over">
                  <RotateCcw />
                </button>
              ) : (
                <span className="ask-milo-palette__esc">ESC</span>
              )}
            </div>

            <div className="ask-milo-palette__suggestions">
              {/* ── Conversation with Milo ───────────────────────────── */}
              {inConversation && (
                <div className="ask-milo-thread">
                  {thread.map((turn, i) => (
                    turn.role === 'user' ? (
                      <div key={i} className="ask-milo-turn ask-milo-turn--user">
                        <span className="ask-milo-turn__q">{turn.text}</span>
                      </div>
                    ) : (
                      <div key={i} className={`ask-milo-turn ask-milo-turn--milo${turn.error ? ' is-error' : ''}`}>
                        <Sparkles className="ask-milo-turn__ic" aria-hidden="true" />
                        <div className="ask-milo-turn__body">
                          {turn.error && <AlertCircle className="ask-milo-turn__warn" aria-hidden="true" />}
                          {turn.text && <div className="ask-milo-turn__a">{turn.text}</div>}

                          {/* Gated web search — host approves before Milo browses. */}
                          {turn.webConfirm && (
                            <div className="ask-milo-webconfirm">
                              <div className="ask-milo-webconfirm__head">
                                <Globe aria-hidden="true" /> Search the web for this?
                              </div>
                              {turn.webConfirm.reason && (
                                <p className="ask-milo-webconfirm__reason">{turn.webConfirm.reason}</p>
                              )}
                              {turn.webConfirm.query && (
                                <p className="ask-milo-webconfirm__query">“{turn.webConfirm.query}”</p>
                              )}
                              <div className="ask-milo-webconfirm__btns">
                                <button
                                  type="button"
                                  className="ask-milo-webconfirm__yes"
                                  onClick={() => submitQuestion(pendingQuestion, { confirmWebSearch: true })}
                                >
                                  <Globe aria-hidden="true" /> Yes, search reliable sources
                                </button>
                                <button
                                  type="button"
                                  className="ask-milo-webconfirm__no"
                                  onClick={() => setThread((t) => [...t, {
                                    role: 'milo',
                                    text: 'No problem — I’ll leave the web out of it. Ask me anything about your event’s details and I’ll answer from your platform.',
                                  }])}
                                >
                                  No thanks
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Provenance — which read-only platform sources Milo used. */}
                          {!turn.error && friendlyTools(turn.usedTools).length > 0 && (
                            <div className="ask-milo-provenance">
                              Milo checked: {friendlyTools(turn.usedTools).join(' · ')}
                            </div>
                          )}

                          {/* Web citations, when Milo searched. */}
                          {turn.citations && turn.citations.length > 0 && (
                            <div className="ask-milo-citations">
                              <span className="ask-milo-citations__label">Sources</span>
                              {turn.citations.slice(0, 5).map((c, ci) => (
                                <a key={ci} href={c.url} target="_blank" rel="noreferrer" className="ask-milo-citation">
                                  <ExternalLink aria-hidden="true" />
                                  <span>{c.title || c.url}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                  {loading && (
                    <div className="ask-milo-turn ask-milo-turn--milo">
                      <Sparkles className="ask-milo-turn__ic" aria-hidden="true" />
                      <div className="ask-milo-turn__body">
                        <div className="ask-milo-thinking">
                          <Loader2 className="ask-milo-thinking__spin" aria-hidden="true" />
                          Milo is thinking…
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={threadEndRef} />
                </div>
              )}

              {/* ── Live search nav (before any conversation) ─────────── */}
              {showSearch && (
                <>
                  {searchResults.guests && searchResults.guests.length > 0 && (
                    <>
                      <div className="ask-milo-palette__section">Guests</div>
                      {searchResults.guests.map((g) => (
                        <button
                          type="button"
                          key={g._id || g.id}
                          className="ask-milo-palette__sugg"
                          onClick={() => {
                            onNavigate('guests');
                            onFocusGuest(g.id || g._id);
                            closePalette();
                          }}
                        >
                          <span className="ask-milo-palette__ic"><Users /></span>
                          <span className="ask-milo-palette__text">
                            {g.first_name} {g.last_name}
                            {g.email ? <span className="ask-milo-palette__hint"> · {g.email}</span> : null}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  {searchResults.vendors && searchResults.vendors.length > 0 && (
                    <>
                      <div className="ask-milo-palette__section">Vendors</div>
                      {searchResults.vendors.map((v) => (
                        <button
                          type="button"
                          key={v._id || v.id}
                          className="ask-milo-palette__sugg"
                          onClick={() => {
                            onNavigate('vendors');
                            closePalette();
                          }}
                        >
                          <span className="ask-milo-palette__ic"><Store /></span>
                          <span className="ask-milo-palette__text">
                            {v.name}
                            {v.category ? <span className="ask-milo-palette__hint"> · {v.category}</span> : null}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                  <div className="ask-milo-palette__section">Ask Milo</div>
                  <button
                    type="button"
                    className="ask-milo-palette__sugg ask-milo-palette__sugg--ask"
                    onClick={() => submitQuestion()}
                  >
                    <span className="ask-milo-palette__ic"><Sparkles /></span>
                    <span className="ask-milo-palette__text">Ask “{searchQuery}”</span>
                    <span className="ask-milo-palette__hint"><CornerDownLeft /></span>
                  </button>
                </>
              )}

              {/* ── Suggested prompts (idle) ──────────────────────────── */}
              {showSuggestions && (
                <>
                  <div className="ask-milo-palette__section">Ask Milo about your event</div>
                  {SUGGESTIONS.map((s, i) => {
                    const Icon = s.ic;
                    return (
                      <button
                        type="button"
                        key={i}
                        className="ask-milo-palette__sugg"
                        onClick={() => submitQuestion(s.text)}
                      >
                        <span className="ask-milo-palette__ic"><Icon /></span>
                        <span className="ask-milo-palette__text">{s.text}</span>
                        {i === 0 && <span className="ask-milo-palette__hint"><CornerDownLeft /></span>}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className="ask-milo-palette__foot">
              <span>Milo answers from your platform · asks before searching the web · never sends or pays without you.</span>
              <span className="ask-milo-palette__shortcut">
                <kbd>⌘</kbd><kbd>K</kbd> to open anywhere
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
